/**
 * useOrganization Hook - Trade Loop v2.0
 *
 * Provides access to organization data and management functions
 * for Grow plan users.
 *
 * Features:
 * - Organization details
 * - Team member management
 * - Invitation management
 * - Seat management
 * - Role checking
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ============================================================================
// TYPES
// ============================================================================

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  ownerId: string;
  teamEnabled: boolean;
  maxTeamMembers: number;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationMember {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'member';
  status: 'active' | 'inactive';
  joinedAt: string;
}

interface OrganizationInvite {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'member';
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  inviteCode: string;
  expiresAt: string;
  createdAt: string;
}

interface SeatInfo {
  total: number;
  included: number;
  additional: number;
  used: number;
  pending: number;
  available: number;
  canPurchaseMore: boolean;
  maxTotal: number;
  costPerAdditional: number;
}

interface OrganizationData {
  organization: Organization | null;
  role: 'admin' | 'member' | null;
  isOwner: boolean;
  needsSetup: boolean;
  seats?: SeatInfo;
}

interface InviteMemberParams {
  email: string;
  firstName: string;
  lastName: string;
  role: 'member';
}

interface CreateOrganizationParams {
  name: string;
  description?: string;
}

interface UpdateOrganizationParams {
  name?: string;
  description?: string;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for accessing and managing organization data
 *
 * @example
 * function MyComponent() {
 *   const {
 *     organization,
 *     isOwner,
 *     members,
 *     invites,
 *     seatInfo,
 *     inviteMember,
 *     removeMember
 *   } = useOrganization();
 *
 *   if (!organization) {
 *     return <CreateOrgPrompt />;
 *   }
 *
 *   return <OrgDashboard members={members} />;
 * }
 */
export function useOrganization() {
  const queryClient = useQueryClient();

  // ========================================
  // Queries
  // ========================================

  // Fetch organization data
  const organizationQuery: UseQueryResult<OrganizationData, Error> = useQuery({
    queryKey: ['organization', 'my-organization'],
    queryFn: async () => {
      const response = await api.get('/api/workflows/organization/my-organization');
      // Handle nested data structure from API
      return response.data?.data || response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  // Fetch members (only if user is admin)
  const membersQuery: UseQueryResult<OrganizationMember[], Error> = useQuery({
    queryKey: ['organization', 'members'],
    queryFn: async () => {
      const response = await api.get('/api/workflows/organization/members');
      const data = response.data?.data || response.data;
      return data?.members || [];
    },
    enabled: organizationQuery.data?.isOwner === true,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch invites (only if user is admin)
  const invitesQuery: UseQueryResult<OrganizationInvite[], Error> = useQuery({
    queryKey: ['organization', 'invites'],
    queryFn: async () => {
      const response = await api.get('/api/workflows/organization/invites');
      const data = response.data?.data || response.data;
      return data?.invites || [];
    },
    enabled: organizationQuery.data?.isOwner === true,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch seat info (only if user is admin)
  const seatsQuery: UseQueryResult<SeatInfo, Error> = useQuery({
    queryKey: ['organization', 'seats'],
    queryFn: async () => {
      const response = await api.get('/api/workflows/organization/seats');
      return response.data?.data || response.data;
    },
    enabled: organizationQuery.data?.isOwner === true,
    staleTime: 2 * 60 * 1000,
  });

  // ========================================
  // Mutations
  // ========================================

  // Create organization
  const createOrganization: UseMutationResult<any, Error, CreateOrganizationParams> = useMutation({
    mutationFn: async (params: CreateOrganizationParams) => {
      const response = await api.post('/api/workflows/organization/create', params);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });

  // Update organization settings
  const updateOrganization: UseMutationResult<any, Error, UpdateOrganizationParams> = useMutation({
    mutationFn: async (params: UpdateOrganizationParams) => {
      const response = await api.put('/api/workflows/organization/settings', params);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', 'my-organization'] });
    },
  });

  // Invite member
  const inviteMember: UseMutationResult<any, Error, InviteMemberParams> = useMutation({
    mutationFn: async (params: InviteMemberParams) => {
      const response = await api.post('/api/workflows/organization/invite', params);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });

  // Revoke invite
  const revokeInvite: UseMutationResult<any, Error, string> = useMutation({
    mutationFn: async (inviteId: string) => {
      const response = await api.delete(`/api/workflows/organization/invite/${inviteId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', 'invites'] });
    },
  });

  // Remove member
  const removeMember: UseMutationResult<any, Error, string> = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await api.delete(`/api/workflows/organization/members/${memberId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });

  // ========================================
  // Helpers
  // ========================================

  // Check if user can invite (has seats available)
  const canInvite = (): boolean => {
    const seatInfo = seatsQuery.data || organizationQuery.data?.seats;
    if (!seatInfo) return false;
    return seatInfo.available > 0;
  };

  // Check if user can purchase more seats
  const canPurchaseSeats = (): boolean => {
    const seatInfo = seatsQuery.data || organizationQuery.data?.seats;
    if (!seatInfo) return false;
    return seatInfo.canPurchaseMore;
  };

  // Get member count
  const getMemberCount = (): number => {
    return membersQuery.data?.length || 0;
  };

  // Get pending invite count
  const getPendingInviteCount = (): number => {
    return invitesQuery.data?.filter(inv => inv.status === 'pending').length || 0;
  };

  // Refresh all organization data
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['organization'] });
  };

  return {
    // Query state
    isLoading: organizationQuery.isLoading,
    isError: organizationQuery.isError,
    error: organizationQuery.error,

    // Organization data
    organization: organizationQuery.data?.organization || null,
    role: organizationQuery.data?.role || null,
    isOwner: organizationQuery.data?.isOwner || false,
    needsSetup: organizationQuery.data?.needsSetup || false,

    // Related data
    members: membersQuery.data || [],
    membersLoading: membersQuery.isLoading,
    invites: invitesQuery.data || [],
    invitesLoading: invitesQuery.isLoading,
    seatInfo: seatsQuery.data || organizationQuery.data?.seats || null,
    seatsLoading: seatsQuery.isLoading,

    // Mutations
    createOrganization,
    updateOrganization,
    inviteMember,
    revokeInvite,
    removeMember,

    // Helpers
    canInvite,
    canPurchaseSeats,
    getMemberCount,
    getPendingInviteCount,
    refresh,
  };
}

export default useOrganization;
